import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { VARIANTS } from '@/engine/variants';
import type { VariantDef } from '@/engine/types';

export const Route = createFileRoute('/rules')({
  component: RulesPage,
});

function RulesPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
      <h1 className="mb-1 font-display text-2xl tracking-wider">Rules</h1>
      <p className="mb-5 text-sm text-muted">
        Every table in the hall, box by box.
      </p>

      <nav aria-label="Variants" className="mb-8 flex flex-wrap gap-2">
        {VARIANTS.map(v => (
          <a
            key={v.id}
            href={`#${v.id}`}
            className="h-8 rounded-full border border-line px-3 text-sm font-semibold leading-8 text-muted hover:border-accent/60 hover:text-text"
          >
            {v.name}
          </a>
        ))}
      </nav>

      <div className="space-y-6">
        {VARIANTS.map(v => (
          <VariantRules key={v.id} variant={v} />
        ))}
      </div>
    </div>
  );
}

function VariantRules({ variant: v }: { variant: VariantDef }) {
  const boxes = v.categories.length * v.columns.length;
  const upper = v.categories.filter(c => c.section === 'upper');
  const lower = v.categories.filter(c => c.section === 'lower');
  return (
    <Card id={v.id} className="scroll-mt-16 p-4 sm:p-6">
      <header className="mb-3">
        <h2 className="font-display text-xl tracking-wide text-accent">
          {v.name}
        </h2>
        <p className="text-sm text-muted">{v.tagline}</p>
      </header>
      <p className="text-sm leading-relaxed">{v.description}</p>
      <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs uppercase tracking-wider text-muted">
        <Fact k="Dice" val={v.diceCount} />
        <Fact k="Rolls" val={v.maxRolls} />
        <Fact k="Boxes" val={boxes} />
        {v.columns.length > 1 && (
          <Fact k="Columns" val={v.columns.map(m => `x${m}`).join(' ')} />
        )}
      </dl>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted">
              <th className="py-1.5 pr-4 font-semibold">Box</th>
              <th className="py-1.5 font-semibold">Scores</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {upper.length > 0 && <GroupRow label="Upper section" />}
            {upper.map(c => (
              <tr key={c.id}>
                <td className="py-1.5 pr-4 font-semibold">{c.name}</td>
                <td className="py-1.5 text-muted">{c.hint}</td>
              </tr>
            ))}
            {v.upperBonusHint && (
              <tr>
                <td className="py-1.5 pr-4 font-semibold text-accent">Bonus</td>
                <td className="py-1.5 text-muted">{v.upperBonusHint}</td>
              </tr>
            )}
            {upper.length > 0 && <GroupRow label="Lower section" />}
            {lower.map(c => (
              <tr key={c.id}>
                <td className="py-1.5 pr-4 font-semibold">{c.name}</td>
                <td className="py-1.5 text-muted">{c.hint}</td>
              </tr>
            ))}
            {v.fiveKind && (
              <tr>
                <td className="py-1.5 pr-4 font-semibold text-accent">Bonus</td>
                <td className="py-1.5 text-muted">
                  +{v.fiveKind.bonus} for each extra five of a kind once that
                  box holds a score
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-muted">
        {v.rules.map(r => (
          <li key={r}>{r}</li>
        ))}
      </ul>
    </Card>
  );
}

function Fact({ k, val }: { k: string; val: string | number }) {
  return (
    <div className="flex gap-1.5">
      <dt>{k}</dt>
      <dd className="text-text">{val}</dd>
    </div>
  );
}

function GroupRow({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={2}
        className="pt-3 pb-1 text-xs font-semibold uppercase tracking-widest text-accent-2"
      >
        {label}
      </td>
    </tr>
  );
}
